import React, { useEffect, useMemo, useState } from "react";
import "../styles/shared-ui.css";
import { useAppContext } from "../context/AppContext";
import { createCity } from "../services/cityService";
import { getCountries } from "../services/countryService";
import { toast } from "../utils/toast";
import { resolveRoleName } from "../utils/auth";

const CityCreatePage = () => {
  const { setActiveSection } = useAppContext();
  const [roleChecked, setRoleChecked] = useState(false);
  const [canAccess, setCanAccess] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let user = null;
        try {
          const raw = sessionStorage.getItem("auth");
          const auth = raw ? JSON.parse(raw) : null;
          user = auth?.user || null;
        } catch {}
        const rn = user ? await resolveRoleName(user) : null;
        const norm = String(rn || "").toUpperCase();
        const allowed = norm === "SUPER_ADMIN" || norm === "SUPERADMIN";
        if (!cancelled) {
          setCanAccess(allowed);
          setRoleChecked(true);
        }
        if (!allowed) setActiveSection("dashboard");
      } catch {
        if (!cancelled) {
          setCanAccess(false);
          setRoleChecked(true);
        }
        setActiveSection("dashboard");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setActiveSection]);

  const [form, setForm] = useState({
    cityName: "",
    cityCode: "",
    countryId: "",
  });
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const setField = (key, val) => setForm((prev) => ({ ...prev, [key]: val }));

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await getCountries();
        setCountries(Array.isArray(res) ? res : []);
      } catch (e) {
        toast.error(e?.message || "Failed to load countries");
        setCountries([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const errors = useMemo(() => {
    const e = {};
    if (!String(form.cityName).trim()) e.cityName = "City name is required";
    if (!String(form.cityCode).trim()) e.cityCode = "City code is required";
    if (!form.countryId) e.countryId = "Country is required";
    return e;
  }, [form]);

  const canSave = useMemo(() => Object.keys(errors).length === 0, [errors]);

  const onSave = async () => {
    if (!canSave || saving) return;
    try {
      setSaving(true);
      await createCity({
        cityName: form.cityName.trim(),
        cityCode: form.cityCode.trim(),
        countryId: Number(form.countryId),
      });
      toast.success("City created successfully!");
      setActiveSection("cities");
    } catch (e) {
      toast.error(e?.message || "Failed to create city");
    } finally {
      setSaving(false);
    }
  };

  const onCancel = () => setActiveSection("cities");

  return (
    <div className="page-container">
      {!roleChecked ?
        <div className="page-content">
          <div className="no-results">Loading...</div>
        </div>
      : !canAccess ?
        <div className="page-content">
          <div className="no-results">Access denied</div>
        </div>
      : <>
          <div className="page-header">
            <div className="header-content">
              <div>
                <h2 className="page-title">Create City</h2>
                <p className="page-subtitle">Add a new city record</p>
              </div>
              <div className="roles-toolbar">
                <button className="secondary-btn" onClick={onCancel}>
                  Cancel
                </button>
                <button
                  className="primary-btn"
                  onClick={onSave}
                  disabled={!canSave || saving}
                >
                  {saving ? "Saving..." : "Create"}
                </button>
              </div>
            </div>
          </div>

          <div className="page-content">
            {loading ?
              <div className="no-results">Loading...</div>
            : <div className="role-card">
                <div
                  className="section-title"
                  style={{ marginBottom: 12, fontWeight: 600 }}
                >
                  Basic Details
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <label>City Name *</label>
                    <input
                      value={form.cityName}
                      onChange={(e) => setField("cityName", e.target.value)}
                      placeholder="e.g. New York"
                    />
                    {errors.cityName && (
                      <div
                        className="input-hint"
                        style={{ color: "#b91c1c", fontSize: 12, marginTop: 4 }}
                      >
                        {errors.cityName}
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label>City Code *</label>
                    <input
                      value={form.cityCode}
                      onChange={(e) => setField("cityCode", e.target.value)}
                      placeholder="e.g. NYC"
                    />
                    {errors.cityCode && (
                      <div
                        className="input-hint"
                        style={{ color: "#b91c1c", fontSize: 12, marginTop: 4 }}
                      >
                        {errors.cityCode}
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Country *</label>
                    <select
                      value={form.countryId}
                      onChange={(e) => setField("countryId", e.target.value)}
                    >
                      <option value="">Select Country</option>
                      {countries.map((c) => (
                        <option
                          key={c.countryId || c.id}
                          value={c.countryId || c.id}
                        >
                          {c.countryName || c.name}
                        </option>
                      ))}
                    </select>
                    {errors.countryId && (
                      <div
                        className="input-hint"
                        style={{ color: "#b91c1c", fontSize: 12, marginTop: 4 }}
                      >
                        {errors.countryId}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            }
          </div>
        </>
      }
    </div>
  );
};

export default CityCreatePage;
